import { clsx } from "clsx";
import type { ReactNode } from "react";
import { Text } from "../typography";

/**
 * Primitive shimmer placeholder. Use for individual skeleton bars/blocks.
 * Pass Tailwind size/shape classes via `className`.
 */
export interface ShimmerProps {
  className?: string;
}

export function Shimmer({ className }: ShimmerProps) {
  return (
    <div
      aria-hidden
      className={clsx("shimmer rounded", className)}
    />
  );
}

/**
 * Composed loading state: one or more shimmer bars plus a short caption
 * explaining what is loading. This is the default loading state across the app.
 */
export interface ShimmerLoaderProps {
  /** Short caption telling the user what is loading. */
  message: string;
  /** Optional richer skeleton (overrides the default bar pattern). */
  children?: ReactNode;
  /** Number of default skeleton lines when `children` is not provided. */
  lines?: number;
  className?: string;
}

export function ShimmerLoader({
  message,
  children,
  lines = 3,
  className,
}: ShimmerLoaderProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={clsx("flex w-full flex-col gap-3", className)}
    >
      {children ?? <DefaultLines lines={lines} />}
      <Text variant="bodySm" tone="muted">
        {message}
      </Text>
    </div>
  );
}

function DefaultLines({ lines }: { lines: number }) {
  const widths = ["w-full", "w-11/12", "w-10/12", "w-9/12", "w-8/12"];
  return (
    <div className="flex w-full flex-col gap-2">
      {Array.from({ length: lines }).map((_, i) => (
        <Shimmer key={i} className={clsx("h-4", widths[i % widths.length])} />
      ))}
    </div>
  );
}
